import React from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { paths } from "../../../routes/paths";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCollectionsSummaryQuery } from "../../collections/collectionsApi";
import { useDashboardPeriod } from "../useDashboardPeriod";
import { money } from "../dashboardHelpers";
import { BRAND, CHART_COLORS } from "../chartColors";
import DashboardSectionCard from "../components/DashboardSectionCard";

// Now driven by the global Period/Compare control (previously hardcoded
// to "Today"), with a paired bar per branch for the comparison window when
// a single-period compare is selected - grouped bars are the standard way
// to add a time dimension to a per-category chart without it competing
// with the branch-vs-branch comparison the chart already does (a second
// bar next to each branch's own, not a second chart/overlay). Multi-year
// compare is intentionally not attempted here (would mean up to 10 bars
// per branch) - Collection Trend is the flagship for multi-year, this
// stays a clean 1-vs-1 comparison per branch.
const DailyCollectionByBranchChart = () => {
  const navigate = useNavigate();
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId } = useBranchFilter();
  const { period, comparison } = useDashboardPeriod();
  const branchId = selectedBranchId ?? undefined;
  const singleCompare = comparison?.type === "single" ? comparison : null;

  const { data, isFetching } = useGetCollectionsSummaryQuery(
    { branchId, dateFrom: period.dateFrom, dateTo: period.dateTo, groupBy: "branch" },
    { skip: !canView }
  );
  const { data: compareData, isFetching: isFetchingCompare } = useGetCollectionsSummaryQuery(
    singleCompare ? { branchId, dateFrom: singleCompare.dateFrom, dateTo: singleCompare.dateTo, groupBy: "branch" } : undefined,
    { skip: !canView || !singleCompare }
  );

  if (!canView) return null;

  const compareByKey = new Map((compareData || []).map((r) => [r.groupKey, r.totalAmount]));
  const chartData = (data || [])
    .map((r) => ({
      name: r.groupLabel,
      current: r.totalAmount,
      // groupBy=branch means groupKey IS the branchId - kept on the datum so a
      // clicked bar can open that branch's own collections for the period.
      branchId: r.groupKey,
      ...(singleCompare ? { compare: compareByKey.get(r.groupKey) ?? 0 } : {}),
    }))
    .sort((a, b) => b.current - a.current);
  const compareHasData = singleCompare && chartData.some((r) => Number(r.compare) > 0);

  return (
    <div className="col-12 col-xl-4">
      <DashboardSectionCard title="Daily Collection by Branch" headerRight={period.label} isLoading={isFetching || isFetchingCompare} isEmpty={chartData.length === 0}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => money(v)} />
            {singleCompare && <Legend wrapperStyle={{ fontSize: 11 }} />}
            <Bar
              dataKey="current"
              name={period.label}
              fill={BRAND}
              radius={[4, 4, 0, 0]}
              cursor="pointer"
              onClick={(datum) => {
                if (datum?.branchId == null) return;
                navigate(paths.collectionsFiltered({ branchId: datum.branchId, dateFrom: period.dateFrom, dateTo: period.dateTo }));
              }}
            />
            {singleCompare && <Bar dataKey="compare" name={singleCompare.label} fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />}
          </BarChart>
        </ResponsiveContainer>
        {singleCompare && !isFetchingCompare && !compareHasData && (
          <div className="small text-muted fst-italic mt-1">No comparison data yet for {singleCompare.label}</div>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default DailyCollectionByBranchChart;
